import { useMemo } from 'react'
import { faSignHanging } from '@fortawesome/pro-light-svg-icons'
import { DataSource, ResidentialPropertyForSaleListing } from '@unreserved-frontend-v2/api/generated/graphql/types'
import { DetailsTableRow } from '@unreserved-frontend-v2/ui/details-table/models'
import { FlexCol } from '@unreserved-frontend-v2/ui/flex/flex-col'
import { emptyDefault } from '@unreserved-frontend-v2/utils/empty-default'
import { massageDate } from '@unreserved-frontend-v2/utils/date-formatters'
import useTranslation from 'next-translate/useTranslation'
import { format } from 'date-fns'

export const useMlsRow = (listing: ResidentialPropertyForSaleListing): DetailsTableRow => {
  const { t } = useTranslation()
  const { mlsDetails, source, updatedAt } = listing

  return useMemo(() => {
    const listingTemplate = (
      <FlexCol className="w-full ">
        {/* This div is only for mobile */}
        <div className="block md:hidden">
          <div className="font-normal">{t('listings:details-pages.mls-listing')}</div>
          <div className="pt-[10px]">{emptyDefault(mlsDetails?.mlsNumber)}</div>
        </div>

        <div className="pt-[15px] md:pt-0">
          <div className="font-normal">{t('listings:details-pages.last-updated')}</div>
          <div className="pt-[10px]">
            {updatedAt ? format(massageDate(updatedAt.split('T')[0]), 'MMMM d, yyyy') : '-'}
          </div>
        </div>

        {/* This div is only for mobile */}
        <div className="block pt-[15px] md:hidden">
          <div className="font-normal">{t('listings:details-pages.brokerage')}</div>
          <div className="pt-[10px]">{emptyDefault(mlsDetails?.brokerageName)}</div>
        </div>

        {source === DataSource.CreaMls && mlsDetails?.mlsListingUrl ? (
          <div className="cursor-pointer pr-1 pt-[10px] md:pt-6">
            <a href={mlsDetails.mlsListingUrl} target="_blank" rel="noreferrer,nofollow">
              <img
                width="125"
                alt="Powered by: REALTOR.ca"
                src="https://www.realtor.ca/images/en-ca/powered_by_realtor.svg"
              />
            </a>
          </div>
        ) : null}
      </FlexCol>
    )

    return {
      icon: faSignHanging,
      title: t('listings:details-pages.listing-brokerage'),
      cells: [
        // This cell doesn not show up in mobile view.
        {
          id: 'listing-brokerage',
          cellClassName: 'hidden md:block',
          data: [
            {
              id: 'listing',
              label: t('listings:details-pages.mls-listing'),
              value: emptyDefault(mlsDetails?.mlsNumber),
            },
            {
              id: 'brokerage',
              label: t('listings:details-pages.brokerage'),
              value: emptyDefault(mlsDetails?.brokerageName),
              valueClassName: 'md:text-ellipsis',
            },
          ],
        },
        // This cell shows up in both mobile and desktop.
        {
          id: 'updated-logo',
          data: [
            {
              id: 'mls-listing',
              value: listingTemplate,
              valueClassName: 'pt-0',
            },
          ],
        },
      ],
    }
  }, [t, mlsDetails?.brokerageName, mlsDetails?.mlsListingUrl, mlsDetails?.mlsNumber, source, updatedAt])
}
